/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Targeting levels", description = "Choose between person-level and household-level targeting in Match Booster to match your campaign goals.", lastUpdatedDate = new Date(1778803200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Targeting levels",
        description: "Choose between person-level and household-level targeting in Match Booster to match your campaign goals.",
        lastUpdatedDate: "Fri May 15 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Person-level matching",
          "depth": 0,
          "id": "person-level-matching"
        }, {
          "value": "Household-level matching",
          "depth": 0,
          "id": "household-level-matching"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      a: "a",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, data teams"
            })]
          })
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Match Booster supports targeting at the person level and household level. Targeting levels are set per sync and determine how Match Booster groups matched identifiers."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["Person-level matching is the default targeting level. Household-level targeting must be enabled in the model's Match Booster configuration (via ", _jsx(_components.a, {
          href: "/match-booster/configure-model#select-expansion-options",
          children: "Household Expansion"
        }), ") before it can be applied to a sync."]
      }), "\n", _jsx(_components.p, {
        children: "Targeting levels cannot be changed after the sync is enabled. If you want to change the targeting level for an audience, create a new sync with the same configuration and set the targeting level before activating it."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "person-level-matching",
        children: "Person-level matching"
      }), "\n", _jsx(_components.p, {
        children: "Person-level matching links your customer and device records to a single individual. Each record maps to one person in the identity graph."
      }), "\n", _jsx(_components.p, {
        children: "Match Booster finds deterministic linkages between your first-party identifiers and people in its third-party identity graph. When matching to devices, Match Booster can isolate the most likely user of a device or broaden the scope by including shared devices in the match criteria (for example, a shared iPad or tablet)."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "household-level-matching",
        children: "Household-level matching"
      }), "\n", _jsx(_components.p, {
        children: "Household-level matching uses Household Expansion to match a single record to a residential address in the identity graph and then expands targeting to reach all of the people and devices in that household."
      }), "\n", _jsx(_components.p, {
        children: "Household Expansion is especially effective for brands and media networks whose products are purchased or used by an entire household:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Retail marketing and retail media networks"
          }), " — Loyalty memberships are often shared across family members. Household Expansion helps target, suppress, and monetize the entire household based on past purchase behavior."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Streaming TV"
          }), " — Household members typically share a single streaming login. Household Expansion targets every member of the household for more effective audience matching and suppression."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Automotive"
          }), " — Vehicle purchases are often family decisions. Household Expansion targets all members of the household based on purchase behavior."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Telecom and utilities"
          }), " — Services like phone, internet, and HVAC are sold at the household level. Household Expansion matches a single customer record to the entire household for more effective suppression and targeting."]
        }), "\n"]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Consider creating separate syncs for person-level and household-level\naudiences so you can compare performance across targeting strategies."
        })
      })]
    });
  }
}
export default MDXContent;
